import { BoxxGenericState, getGenericInitialState } from "@boxx/core";
import {
  IStoreProductsStateError,
  IStoreProductsStateSuccess,
  ProductModel,
  StoreProductsStateType,
} from "@boxx/store-core";
import { ActionReducer, createReducer, on } from "@ngrx/store";
import * as fromProductsActions from "./store-products.actions";

export interface IStoreProductsState
  extends BoxxGenericState<
    ProductModel[],
    IStoreProductsStateSuccess,
    IStoreProductsStateError
  > {
  selectedProduct: ProductModel;
}

export const productsInitialState: IStoreProductsState = {
  ...getGenericInitialState(),
  data: [],
  selectedProduct: null,
};

export const storeProductsReducer: ActionReducer<IStoreProductsState> =
  createReducer(
    productsInitialState,
    on(
      fromProductsActions.loadProductsBeginAction,
      fromProductsActions.deleteProductBeginAction,
      (state): IStoreProductsState => ({
        ...state,
        isLoading: true,
        error: null,
        success: null,
      })
    ),
    on(
      fromProductsActions.loadProductByIdBeginAction,
      fromProductsActions.createProductBeginAction,
      fromProductsActions.updateProductBeginAction,
      fromProductsActions.loadSingleProductBeginAction,
      fromProductsActions.deleteImageProductBeginAction,
      (state): IStoreProductsState => ({
        ...state,
        //isLoading: true,
        error: null,
        success: null,
      })
    ),
    on(
      fromProductsActions.loadProductsSuccessAction,
      fromProductsActions.deleteProductSuccessAction,
      (state, action): IStoreProductsState => ({
        ...state,
        isLoading: false,
        fetched: true,
        data: action.payload,
        error: null,
        success: { after: getStoreProductsActionType(action.type) },
      })
    ),
    on(
      fromProductsActions.loadProductByIdSuccessAction,
      (state, action): IStoreProductsState => ({
        ...state,
        //isLoading: false,
        data: null,
        //selectedProduct: action.payload,
        error: null,
        success: { after: getStoreProductsActionType(action.type) },
      })
    ),

    on(
      fromProductsActions.loadSingleProductSuccessAction,
      (state, action): IStoreProductsState => ({
        ...state,
        //isLoading: false,
        fetched: true,
        data: action.payload,
        error: null,
        success: { after: getStoreProductsActionType(action.type) },
      })
    ),

    on(
      fromProductsActions.loadUpdatedProductSuccessAction,
      (state, action): IStoreProductsState => ({
        ...state,
        //isLoading: false,
        fetched: true,
        data: state.data.map((prod) => {
          if (prod.id === action.payload.id) {
            return {
              ...action.payload,
            };
          }
          return prod;
        }),
        error: null,
        success: { after: getStoreProductsActionType(action.type) },
      })
    ),

    on(
      fromProductsActions.loadProductByIdSuccessAction,
      (state, action): IStoreProductsState => ({
        ...state,
        //isLoading: false,
        data: state.data.map((prod) => {
          if (prod.id === action.payload.id) {
            return {
              ...action.payload,
            };
          }
          return prod;
        }),
        //selectedProduct: action.payload,
        error: null,
        success: { after: getStoreProductsActionType(action.type) },
      })
    ),
    on(
      fromProductsActions.deleteImageProductSuccessAction,
      (state, action) =>
        Object.assign({}, state, {
          isLoading: false,
          fetched: true,
          error: null,
          success: {
            after: getStoreProductsActionType(action.type),
            data: state.data.map((prod) => {
              if (prod.id === action.payload.id) {
                return {
                  ...prod,
                  media: action.payload.imageList
                };
              }
              return prod;
            }),
          }          
        })
    ),
    on(
      fromProductsActions.createProductSuccessAction,
      (state, action): IStoreProductsState => ({
        ...state,
        //isLoading: false,
        fetched: true,
        data: action.payload.products,
        error: null,
        //success: { after: getStoreProductsActionType(action.type) },
        success: {
          after: getStoreProductsActionType(action.type),
          data: action.payload.newProduct,
        },
      })
    ),
    on(fromProductsActions.updateProductSuccessAction, (state, action) =>
      Object.assign({}, state, {
        //isLoading: false,
        fetched: true,
        error: null,
        success: { after: getStoreProductsActionType(action.type) },
        data: state.data.map((prod) => {
          if (prod.id === action.payload.id) {
            return {
              ...action.payload,
            };
          }
          return prod;
        }),
      })
    ),
    on(
      fromProductsActions.loadProductsFailAction,
      fromProductsActions.deleteProductFailAction,
      (state, action): IStoreProductsState => ({
        ...state,
        isLoading: false,
        error: {
          after: getStoreProductsActionType(action.type),
          error: action.errors,
        },
        success: null,
      })
    ),
    on(
      fromProductsActions.loadProductByIdFailAction,
      fromProductsActions.createProductFailAction,
      fromProductsActions.updateProductFailAction,
      fromProductsActions.loadSingleProductFailAction,
      fromProductsActions.deleteImageProductFailAction,
      (state, action): IStoreProductsState => ({
        ...state,
        //isLoading: false,
        error: {
          after: getStoreProductsActionType(action.type),
          error: action.errors,
        },
        success: null,
      })
    ),
    on(fromProductsActions.clearProductsSuccessErrorDataAction, (state) => ({
      ...state,
      error: null,
      success: null,
    }))
  );

const getStoreProductsActionType = (
  type: fromProductsActions.StoreProductsActionTypes
) => {
  let action: StoreProductsStateType;

  switch (type) {
    case fromProductsActions.StoreProductsActionTypes.loadProductsSuccess:
    case fromProductsActions.StoreProductsActionTypes.loadProductsFail:
      action = "LOAD_PRODUCTS";
      break;
    case fromProductsActions.StoreProductsActionTypes.createProductSuccess:
    case fromProductsActions.StoreProductsActionTypes.createProductFail:
      action = "CREATE_PRODUCT";
      break;
    case fromProductsActions.StoreProductsActionTypes.updateProductSuccess:
    case fromProductsActions.StoreProductsActionTypes.updateProductFail:
      action = "UPDATE_PRODUCT";
      break;
    case fromProductsActions.StoreProductsActionTypes.deleteProductSuccess:
    case fromProductsActions.StoreProductsActionTypes.deleteProductFail:
      action = "DELETE_PRODUCT";
      break;
    case fromProductsActions.StoreProductsActionTypes.loadSingleProductSuccess:
    case fromProductsActions.StoreProductsActionTypes.loadSingleProductFail:
      action = "GET_SINGLE_PRODUCT";
      break;
    case fromProductsActions.StoreProductsActionTypes.loadUpdatedProductSuccess:
        case fromProductsActions.StoreProductsActionTypes.loadUpdatedProductFail:
          action = "GET_UPDATED_PRODUCT";
          break;
    case fromProductsActions.StoreProductsActionTypes.deleteImageProductSuccess:
    case fromProductsActions.StoreProductsActionTypes.deleteImageProductFail:
      action = "DELETE_IMAGE_PRODUCT";
      break;
    default:
      action = "UNKNOWN";
  }

  return action;
};
